import React, { type FC } from 'react';

import type { SidebarNavigationProps } from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import { type TranslateFunction } from 'react-localize-redux';

import type { BooleanFeatureFlagResolution } from '../../../../services/featureFlags/getFeatureFlagBooleanDetails';
import { type PortalUser, shouldDisplay } from '../../../../viewConfiguration';
import { type ItemMetaData } from '../menuItemsConfig';

// ---------------------------------------------------------------------------
// Inputs to useMenuItemsConfig
// ---------------------------------------------------------------------------

export type SalesOrdersNavChoice = 'pending' | 'sales' | 'orders';

/**
 * Hide both Sales and Orders until the org-keyed provider is ready. Once
 * resolved: on / FLAG_NOT_FOUND → Sales, off → Orders. flipdish-global always
 * shows Sales (cross-org view). forceSales short-circuits to Sales.
 */
export const getSalesOrdersNavChoice = (
  appId: string | undefined | null,
  resolution: BooleanFeatureFlagResolution,
  forceSales = false
): SalesOrdersNavChoice => {
  if (forceSales || appId === 'flipdish-global') {
    return 'sales';
  }
  if (resolution.status === 'pending') {
    return 'pending';
  }
  return resolution.value ? 'sales' : 'orders';
};

type OmitableMenuItem = {
  omit?: boolean;
  subItems?: OmitableMenuItem[];
};

/**
 * Removes menu entries marked with `omit: true` from the top level and nested
 * `subItems`. Parents are kept unless they themselves are omitted.
 */
export const removeOmittedMenuItems = <T extends OmitableMenuItem>(items: T[]): T[] =>
  items
    .filter((item) => !item.omit)
    .map((item) =>
      item.subItems ? { ...item, subItems: removeOmittedMenuItems(item.subItems) } : item
    );

// ---------------------------------------------------------------------------
// From config entries to ui-library SidebarNavigation items
// ---------------------------------------------------------------------------

export type SidebarNavItem = SidebarNavigationProps['items'][number];
export type SidebarSubItem = NonNullable<SidebarNavItem['items']>[number];
export type NavIcon = FC<{ className?: string }>;
type SubItem = NonNullable<ItemMetaData['subItems']>[number];
type NavEntry = Pick<
  ItemMetaData,
  'hidden' | 'path' | 'permissions' | 'viewId' | 'exemptFromViewConfiguration'
>;

export type TransformOptions = {
  /** Called with each visible `isMFE` href; library items carry only `href`, so the host tracks MFE targets itself. */
  onMfeHref?: (href: string) => void;
};

/**
 * Wraps a rendered JSX element into an FC<{className?: string}> that
 * SidebarNavigation can mount with its own className for sizing/colour.
 */
export const wrapIcon = (element: React.ReactElement): NavIcon => {
  const WrappedIcon: NavIcon = ({ className }) => React.cloneElement(element, { className });
  return WrappedIcon;
};

/**
 * Whether the viewer sees this entry. `hidden` entries never render. Every entry, including a
 * pathless group, goes through `shouldDisplay`; `transformItems` then also drops a pathless group
 * with no visible children.
 */
const isVisible = (item: NavEntry, portalUser: PortalUser): boolean => {
  if (item.hidden) return false;
  return shouldDisplay(portalUser, item.viewId, {
    legacyVisibilityPermissions: item.permissions,
    exemptFromViewConfiguration: item.exemptFromViewConfiguration,
  });
};

export const transformSubItems = (
  subItems: SubItem[],
  portalUser: PortalUser,
  translate: TranslateFunction,
  options: TransformOptions = {}
): SidebarSubItem[] => {
  return subItems
    .filter((item) => isVisible(item, portalUser))
    .map((item) => {
      if (item.isMFE) options.onMfeHref?.(item.path);
      return {
        label: translate(item.title) as string,
        href: item.path,
        ...(item.icon ? { icon: wrapIcon(item.icon) } : {}),
        ...(item.isBeta ? { badge: translate('Beta') as string } : {}),
      };
    });
};

type ItemToggle = NonNullable<SidebarNavItem['toggle']>;

export const transformItems = (
  rawItems: ItemMetaData[],
  portalUser: PortalUser,
  translate: TranslateFunction,
  getToggle?: (item: ItemMetaData) => ItemToggle | undefined,
  options: TransformOptions = {}
): SidebarNavItem[] => {
  return rawItems
    .filter((item) => isVisible(item, portalUser))
    .map((item): SidebarNavItem | null => {
      const subItems = item.subItems
        ? transformSubItems(item.subItems, portalUser, translate, options)
        : undefined;

      // Parent-only items (no href) with no visible children should be dropped
      if (!item.path && (!subItems || subItems.length === 0)) {
        return null;
      }

      if (item.path && item.isMFE) options.onMfeHref?.(item.path);

      const toggle = getToggle?.(item);

      return {
        label: translate(item.title) as string,
        ...(item.path ? { href: item.path } : {}),
        icon: wrapIcon(item.icon),
        ...(subItems?.length ? { items: subItems } : {}),
        ...(item.isBeta ? { badge: translate('Beta') as string } : {}),
        ...(toggle ? { toggle } : {}),
      };
    })
    .filter((item): item is SidebarNavItem => item !== null);
};
